import React, { KeyboardEvent } from 'react';
import { useCopyClipboard } from '../../providers/useCopyClipboard';
import { Tooltip } from '../../Tooltip/Tooltip';
import { TooltipProps } from '../../Tooltip/Tooltip.types';
import { st, classes } from './InlineVariant.st.css';
import {
  COPY_RESET_TIMEOUT_MS,
  DEFAULT_TOOLTIP_COPIED,
  DEFAULT_TOOLTIP_IDLE,
  DATA_HOOKS,
} from '../CodeSnippet.constants';
import { CodeSnippetSize } from '../CodeSnippet.types';

type InlineVariantProps = {
  code: string;
  size?: CodeSnippetSize;
  copyText?: string;
  disableCopy?: boolean;
  copyTooltipText?: string;
  copiedTooltipText?: string;
  tooltipProps?: Omit<Partial<TooltipProps>, 'content'>;
  onCopy?: (copiedValue: string) => void;
  ariaLabel?: string;
  dataHook?: string;
  className?: string;
};

const InlineVariant = ({
  code,
  size,
  copyText,
  disableCopy = false,
  copyTooltipText = DEFAULT_TOOLTIP_IDLE,
  copiedTooltipText = DEFAULT_TOOLTIP_COPIED,
  tooltipProps,
  onCopy,
  ariaLabel,
  dataHook,
  className,
}: InlineVariantProps) => {
  const valueToCopy = copyText ?? code;

  const { isCopied, copyToClipboard } = useCopyClipboard({
    value: valueToCopy,
    onCopy: () => onCopy?.(valueToCopy),
    resetTimeout: COPY_RESET_TIMEOUT_MS,
  });

  if (disableCopy) {
    return (
      <span
        data-hook={dataHook}
        aria-label={ariaLabel}
        className={st(classes.root, { size, static: true }, className)}
      >
        <code data-hook={DATA_HOOKS.code} className={classes.code}>
          {code}
        </code>
      </span>
    );
  }

  const tooltipLabel = isCopied ? copiedTooltipText : copyTooltipText;

  const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      copyToClipboard();
    }
  };

  return (
    <Tooltip appendTo="window" inline {...tooltipProps} content={tooltipLabel}>
      <span
        role="button"
        tabIndex={0}
        data-hook={dataHook}
        aria-label={ariaLabel ?? `${code}, ${tooltipLabel}`}
        className={st(classes.root, { size }, className)}
        onClick={copyToClipboard}
        onKeyDown={handleKeyDown}
      >
        <code data-hook={DATA_HOOKS.code} className={classes.code}>
          {code}
        </code>
      </span>
    </Tooltip>
  );
};

InlineVariant.displayName = 'CodeSnippet.InlineVariant';

export { InlineVariant };
